import { resolveAnchor } from 'pages/ApplicationDetailsPage/components/Essay/utils/resolveAnchor'

export function parseJson(raw) {
	if (!raw) return null
	if (typeof raw === 'object') return raw
	try {
		return JSON.parse(raw)
	} catch {
		return null
	}
}

export function formatUpdatedAt(value) {
	if (!value) return ''
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return ''
	return date.toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	})
}

const MS_IN_MIN = 1000 * 60
const MS_IN_HOUR = MS_IN_MIN * 60
const MS_IN_DAY = MS_IN_HOUR * 24

export function formatCardDateShort(value) {
	if (!value) return ''
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return ''
	const dd = String(date.getDate()).padStart(2, '0')
	const mm = String(date.getMonth() + 1).padStart(2, '0')
	const yy = String(date.getFullYear()).slice(-2)
	return `${dd}/${mm}/${yy}`
}

export function formatFeedbackCardTime(value) {
	if (!value) return ''
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return ''
	const diffMs = Math.max(0, Date.now() - date.getTime())
	if (diffMs < MS_IN_HOUR) {
		const mins = Math.max(1, Math.floor(diffMs / MS_IN_MIN))
		return `${mins} minute${mins === 1 ? '' : 's'} ago`
	}
	if (diffMs < MS_IN_DAY) {
		const hours = Math.max(1, Math.floor(diffMs / MS_IN_HOUR))
		return `${hours} hour${hours === 1 ? '' : 's'} ago`
	}
	const days = Math.max(1, Math.floor(diffMs / MS_IN_DAY))
	if (days <= 14) {
		return `Waiting ${days} day${days === 1 ? '' : 's'}`
	}
	return date.toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	})
}

export function formatFeedbackCardTimeShort(value) {
	if (!value) return ''
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return ''
	const diffMs = Math.max(0, Date.now() - date.getTime())
	if (diffMs < MS_IN_HOUR) {
		const mins = Math.max(1, Math.floor(diffMs / MS_IN_MIN))
		return `${mins} min ago`
	}
	if (diffMs < MS_IN_DAY) {
		const hours = Math.max(1, Math.floor(diffMs / MS_IN_HOUR))
		return `${hours} hour${hours === 1 ? '' : 's'} ago`
	}
	const days = Math.max(1, Math.floor(diffMs / MS_IN_DAY))
	if (days <= 14) {
		return `Waiting ${days}d`
	}
	return formatCardDateShort(value)
}

function messageTime(m) {
	const raw = m?.created_at || m?.updated_at
	const ms = raw ? new Date(raw).getTime() : 0
	return Number.isFinite(ms) ? ms : 0
}

export function formatFeedbackMessageTime(value) {
	if (!value) return ''
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return ''
	return date.toLocaleTimeString('en-US', {
		hour: 'numeric',
		minute: '2-digit',
		hour12: true,
	})
}

export function mapMessagesToFeedbackItems(messages, versionContentText) {
	const list = Array.isArray(messages) ? [...messages] : []
	list.sort((a, b) => {
		const diff = messageTime(a) - messageTime(b)
		if (diff !== 0) return diff
		return String(a?.id || '').localeCompare(String(b?.id || ''))
	})
	return list.map(m => {
		const createdAt = m?.created_at || m?.updated_at || null
		return {
			id: m.id,
			anchor: m.anchor || null,
			resolvedAnchor: m.anchor
				? resolveAnchor(m.anchor, versionContentText || '')
				: null,
			senderType: m.sender_type === 'student' ? 'student' : 'counselor',
			author: m.sender_type === 'student' ? 'Student' : 'Counselor',
			text: m.message_text || '',
			isUnread: m.sender_type === 'student' && !m.is_read_by_counselor,
			createdAt,
			timeLabel: formatFeedbackMessageTime(createdAt),
		}
	})
}

/** Avoid UI flicker on silent polls when nothing changed. */
export function areFeedbackItemsEqual(prev = [], next = []) {
	if (prev === next) return true
	if (prev.length !== next.length) return false
	for (let i = 0; i < prev.length; i += 1) {
		const a = prev[i]
		const b = next[i]
		if (String(a?.id) !== String(b?.id)) return false
		if (Boolean(a?.isUnread) !== Boolean(b?.isUnread)) return false
		if (String(a?.text || '') !== String(b?.text || '')) return false
		if (String(a?.anchor?.text || '') !== String(b?.anchor?.text || '')) {
			return false
		}
	}
	return true
}
